import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutGrid, Search, Square, SlidersHorizontal, X } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSaveToggle } from "@/hooks/use-save-toggle";
import { MatchCard } from "@/components/match-card";
import { DailyDrop } from "@/components/daily-drop";
import { MembershipBanner } from "@/components/membership-card";
import { ConfirmRequestDialog } from "@/components/confirm-request";
import { FiltersDialog, filterCount, filtersToQuery } from "@/components/filters-dialog";
import { Button, Chip, EmptyState, ErrorState, PageHeader } from "@/components/ui";

export default function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wide, setWide] = useState(false);
  const [target, setTarget] = useState(null);
  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const toggleSave = useSaveToggle();
  const nFilters = filterCount(filters);
  const { data: options } = useQuery({ queryKey: ["options"], queryFn: () => api("/options", { auth: false }) });
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["matches", filters], queryFn: () => api(`/matches${filtersToQuery(filters)}`) });
  const count = data?.count ?? 0;
  const subtitle = isLoading ? "Finding compatible members…" : nFilters
    ? `${count} ${count === 1 ? "member matches" : "members match"} your filters`
    : `${count} compatible ${count === 1 ? "member" : "members"} for you today`;
  const iconBtn = "flex h-11 w-11 items-center justify-center rounded-full border transition-colors";

  return (
    <div data-testid="discover-screen">
      <PageHeader title="Discover Your LOVE NET Matches." subtitle={subtitle} right={
        <div className="flex gap-2">
          <button data-testid="discover-filters-button" aria-label="Filters" onClick={() => setFiltersOpen(true)} className={`${iconBtn} ${nFilters ? "border-primary bg-primary text-white" : "border-border bg-white text-primary hover:bg-[#F6E3E4]"}`}>
            <SlidersHorizontal className="h-5 w-5" />
          </button>
          <button data-testid="discover-layout-toggle" aria-label={wide ? "Grid view" : "Single profile view"} onClick={() => setWide(!wide)} className={`${iconBtn} border-border bg-white text-primary hover:bg-[#F6E3E4]`}>
            {wide ? <LayoutGrid className="h-5 w-5" /> : <Square className="h-5 w-5" />}
          </button>
        </div>
      } />
      {nFilters ? (
        <div className="mb-6 flex gap-2">
          <Chip testId="discover-active-filters-chip" label={`${nFilters} filter${nFilters > 1 ? "s" : ""} on`} selected icon={SlidersHorizontal} onClick={() => setFiltersOpen(true)} />
          <Chip testId="discover-clear-filters-chip" label="Clear filters" icon={X} onClick={() => setFilters({})} />
        </div>
      ) : null}
      <MembershipBanner membership={user?.membership} />
      {!nFilters ? <DailyDrop requestLabel={user?.request_label} onRequest={setTarget} /> : null}
      {!nFilters && count > 0 ? <h2 className="mb-4 text-[28px]">More compatible members</h2> : null}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <div key={i} data-testid={`discover-skeleton-${i}`} className="aspect-[3/4.6] animate-pulse rounded-[20px] bg-[#F4EBE6]" />)}
        </div>
      ) : isError ? (
        <ErrorState testId="discover-error" message={error.message} onRetry={() => refetch()} />
      ) : count === 0 ? (
        <EmptyState testId="discover-empty" icon={Search} title="No matches found yet."
          text={nFilters ? "No eligible members match these filters. Try clearing some filters." : "We only show members who fit your preferences. Try widening your age range or choosing “No preference” on some fields."}
          action={
            <div className="mt-6 flex w-full flex-col gap-3">
              <Button testId="discover-refresh-button" title="Refresh" variant="outline" onClick={() => refetch()} />
              <Button testId="discover-edit-preferences-button" title="Edit preferences" onClick={() => navigate("/onboarding?mode=edit&step=2")} />
            </div>
          } />
      ) : (
        <>
          {count < 5 ? <p className="mb-4 text-sm text-muted-foreground" data-testid="discover-few-note">Showing every eligible member right now. New members join daily.</p> : null}
          <div className={wide ? "mx-auto grid max-w-lg grid-cols-1 gap-6" : "grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4"} data-testid="discover-grid">
            {data.matches.map((m, i) => (
              <MatchCard key={m.id} index={i} profile={m} wide={wide} requestLabel={user?.request_label ?? data.request_label}
                onView={() => navigate(`/member/${m.id}`)} onRequest={() => setTarget(m)} onToggleSave={() => toggleSave(m.id, m.saved)} />
            ))}
          </div>
        </>
      )}
      <ConfirmRequestDialog target={target} onClose={() => setTarget(null)} />
      {options ? <FiltersDialog key={JSON.stringify(filters) + filtersOpen} open={filtersOpen} onClose={() => setFiltersOpen(false)} value={filters} onApply={setFilters} options={options} /> : null}
    </div>
  );
}
